import { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ComicCard from '@/components/feature/ComicCard';
import PageSEO from '@/components/feature/PageSEO';
import { comics, categories } from '@/mocks/comics';
import type { Comic } from '@/mocks/comics';

export default function ComicDetail() {
  const { id } = useParams<{ id: string }>();

  const comic = useMemo(() => comics.find((c) => c.id === id), [id]);

  const relatedComics = useMemo(() => {
    if (!comic) return [];
    return comics
      .filter((c) => c.id !== comic.id && c.category === comic.category)
      .slice(0, 6);
  }, [comic]);

  const categoryInfo = useMemo(
    () => categories.find((c) => c.slug === comic?.category),
    [comic]
  );

  if (!comic) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="flex flex-col items-center justify-center py-32">
          <div className="w-20 h-20 flex items-center justify-center rounded-full bg-background-100 mb-4">
            <i className="ri-file-unknow-line text-3xl text-foreground-400"></i>
          </div>
          <h2 className="text-lg font-bold font-heading text-foreground-800 mb-2">
            漫画未找到
          </h2>
          <p className="text-sm text-foreground-500 mb-4">
            抱歉，您要查看的漫画不存在或已被移除。
          </p>
          <Link
            to="/"
            className="px-6 py-2 bg-primary-500 text-background-50 rounded-full text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            返回首页
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'ComicSeries',
    name: comic.title,
    author: {
      '@type': 'Person',
      name: comic.author,
    },
    description: comic.description,
    url: `https://www.hanmanwang.com/comic/${comic.id}`,
    genre: comic.tags,
    image: comic.coverImage,
    datePublished: '2024-01-01',
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      <PageSEO
        title={`${comic.title}_${comic.author}_免费韩漫在线阅读_韩漫网`}
        description={`${comic.title} - ${comic.description.slice(0, 100)}，韩漫网提供高清下拉式免费在线阅读。`}
        keywords={`${comic.title},${comic.author},免费韩漫,${comic.tags.join(',')}`}
        canonicalUrl={`https://www.hanmanwang.com/comic/${comic.id}`}
        ogType="article"
      />
      <ScrollToTop />
      <div className="min-h-screen bg-background-50">
        <Navbar />

        <Breadcrumb
          items={[
            { label: '首页', path: '/' },
            { label: '漫画分类', path: '/category/all' },
            ...(categoryInfo
              ? [{ label: categoryInfo.name, path: `/category/${categoryInfo.slug}` }]
              : []),
            { label: comic.title },
          ]}
        />

        {/* Comic Detail Hero */}
        <section className="relative">
          {/* Background blur */}
          <div className="absolute inset-0 overflow-hidden">
            <img
              src={comic.coverImage}
              alt=""
              className="w-full h-full object-cover object-top blur-3xl opacity-20 scale-110"
            />
          </div>

          <div className="relative px-4 md:px-6 py-8 md:py-12">
            <div className="flex flex-col md:flex-row gap-6 md:gap-10">
              {/* Cover */}
              <div className="w-[180px] h-[250px] md:w-[220px] md:h-[310px] rounded-lg overflow-hidden shrink-0 mx-auto md:mx-0">
                <img
                  src={comic.coverImage}
                  alt={`${comic.title} - 韩漫免费阅读`}
                  title={`${comic.title} - 韩漫网`}
                  className="w-full h-full object-cover object-top"
                />
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-3">
                  {comic.title}
                </h1>

                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <span className="text-sm text-foreground-600">{comic.author}</span>
                  <span className="text-foreground-300">|</span>
                  <span
                    className={`px-2 py-0.5 rounded text-xs font-medium ${
                      comic.status === '连载中'
                        ? 'bg-primary-100 text-primary-700'
                        : 'bg-accent-100 text-accent-700'
                    }`}
                  >
                    {comic.status}
                  </span>
                  <span className="text-foreground-300">|</span>
                  <span className="text-sm text-foreground-600">共{comic.totalChapters}话</span>
                  <span className="text-foreground-300">|</span>
                  <span className="text-sm text-foreground-600">
                    更新于 {comic.updatedAt}
                  </span>
                </div>

                {/* Stats */}
                <div className="flex items-center gap-6 mb-5">
                  <div className="flex items-center gap-1">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-star-fill text-sm text-primary-500"></i>
                    </div>
                    <span className="text-lg font-bold text-primary-500">{comic.rating}</span>
                    <span className="text-xs text-foreground-500">分</span>
                  </div>
                  <div className="flex items-center gap-1 text-sm text-foreground-600">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-eye-line text-sm"></i>
                    </div>
                    <span>
                      {(comic.views / 10000).toFixed(0)}万 阅读
                    </span>
                  </div>
                  <div className="flex items-center gap-1 text-sm text-foreground-600">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-heart-line text-sm"></i>
                    </div>
                    <span>
                      {(comic.favorites / 10000).toFixed(0)}万 收藏
                    </span>
                  </div>
                </div>

                {/* Tags */}
                <div className="flex items-center gap-2 mb-5 flex-wrap">
                  {comic.tags.map((tag) => (
                    <Link
                      key={tag}
                      to={`/category/${comic.category}`}
                      className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs hover:bg-secondary-200 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      {tag}
                    </Link>
                  ))}
                </div>

                {/* Description */}
                <article className="bg-background-100 rounded-lg p-4 mb-5">
                  <h3 className="text-sm font-bold font-heading text-foreground-800 mb-2">
                    作品简介
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {comic.description}
                  </p>
                </article>

                {/* Action Buttons */}
                <div className="flex items-center gap-3">
                  <button className="px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap">
                    <div className="w-4 h-4 inline-flex items-center justify-center mr-1">
                      <i className="ri-play-circle-line text-sm"></i>
                    </div>
                    开始阅读
                  </button>
                  <button className="px-6 py-2.5 bg-background-100 text-foreground-700 rounded-full text-sm font-medium hover:bg-background-200 transition-all cursor-pointer whitespace-nowrap">
                    <div className="w-4 h-4 inline-flex items-center justify-center mr-1">
                      <i className="ri-heart-line text-sm"></i>
                    </div>
                    收藏
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Chapters */}
        <section className="px-4 md:px-6 py-8 md:py-10 bg-background-100">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-lg md:text-xl font-bold font-heading text-foreground-900">
              <strong>章节列表</strong>
              <span className="text-sm font-normal text-foreground-500 ml-2">
                共 {comic.totalChapters} 话
              </span>
            </h2>
            <div className="flex items-center gap-2">
              <button className="px-3 py-1.5 rounded-full text-xs bg-background-50 text-foreground-600 hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap">
                正序
              </button>
              <button className="px-3 py-1.5 rounded-full text-xs bg-primary-500 text-background-50 cursor-pointer whitespace-nowrap">
                倒序
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
            {comic.chapters.map((chapter, index) => (
              <a
                key={chapter.id}
                href="#"
                className="flex items-center justify-between px-4 py-3 bg-background-50 rounded-lg hover:bg-primary-50 transition-colors cursor-pointer group"
              >
                <span className="text-sm text-foreground-700 group-hover:text-primary-600 transition-colors truncate">
                  {chapter.title}
                </span>
                <span className="text-xs text-foreground-400 shrink-0 ml-2">
                  {chapter.updatedAt}
                </span>
              </a>
            ))}
          </div>
        </section>

        {/* Related Comics */}
        {relatedComics.length > 0 && (
          <section className="px-4 md:px-6 py-10 md:py-14">
            <h2 className="text-xl font-bold font-heading text-foreground-900 mb-6">
              <strong>相关推荐</strong>
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {relatedComics.map((c) => (
                <ComicCard key={c.id} comic={c} size="medium" />
              ))}
            </div>
          </section>
        )}

        <Footer />
      </div>
    </>
  );
}